import { type CasesAlertsVariables } from '@repo/internal-tools-api/src/services/cases/CasesAlertsService.ts'
import { type CaseType } from '@repo/internal-tools-api/src/services/cases/types.ts'
import type { TabOption } from '@repo/internal-tools-design-system/src/lib/Tabs/Tabs.tsx'
import type {
  EnumKey,
  ObjVal,
} from '@repo/internal-tools-type-utils/src/utils.ts'
import type { PaginationState } from '@tanstack/react-table'
import {
  type CaseStepsFilterOptionValue,
  getDefaultCaseStepFilterValues,
} from '../CaseTableTile/CaseTable/CaseStepColumnFilterPopoverUi/caseStepOptions.ts'

export const INITIAL_ALERT_TABLE_PAGINATION_STATE = {
  pageIndex: 0,
  pageSize: 25,
} satisfies PaginationState

export type AlertFilterType = NonNullable<CasesAlertsVariables>['filterType']

export const ALERT_TABLE_TILE_TABS = {
  MISDIRECTED_DEPOSIT: 'MisdirectedDeposit',
  FIN_CRIMES_TRIAGE: 'FinCrimesTriage',
  PROOF_OF_AUTH: 'ProofOfAuth',
  VISA_RISK_MANAGER: 'VisaRiskManager',
} satisfies Record<EnumKey, AlertFilterType>

export type AlertTableTileTabs = typeof ALERT_TABLE_TILE_TABS
export type AlertTableTileTab = ObjVal<AlertTableTileTabs>
export type AlertTableTileTabOption = TabOption<AlertTableTileTab>

export const composeAlertTabOptions = (
  allowedCaseTypes: CaseType[],
): Array<AlertTableTileTabOption> => {
  const tabOptions: Array<AlertTableTileTabOption> = []

  if (allowedCaseTypes.includes('MisdirectedDeposit')) {
    tabOptions.push({
      id: ALERT_TABLE_TILE_TABS.MISDIRECTED_DEPOSIT,
      label: 'FraudOps',
    })
  }

  if (allowedCaseTypes.includes('FinCrimes')) {
    tabOptions.push({
      id: ALERT_TABLE_TILE_TABS.FIN_CRIMES_TRIAGE,
      label: 'FinCrimes',
    })
  }

  if (allowedCaseTypes.includes('ProofOfAuth')) {
    tabOptions.push({
      id: ALERT_TABLE_TILE_TABS.PROOF_OF_AUTH,
      label: 'POA',
    })
  }

  if (allowedCaseTypes.includes('VisaRiskManager')) {
    tabOptions.push({
      id: ALERT_TABLE_TILE_TABS.VISA_RISK_MANAGER,
      label: 'VRM',
    })
  }

  return tabOptions
}

export const getDefaultAlertTableTileTab = (
  allowedCaseTypes: CaseType[],
): AlertTableTileTab | undefined => {
  if (allowedCaseTypes.includes('MisdirectedDeposit')) {
    return ALERT_TABLE_TILE_TABS.MISDIRECTED_DEPOSIT
  } else if (allowedCaseTypes.includes('FinCrimes')) {
    return ALERT_TABLE_TILE_TABS.FIN_CRIMES_TRIAGE
  } else if (allowedCaseTypes.includes('ProofOfAuth')) {
    return ALERT_TABLE_TILE_TABS.PROOF_OF_AUTH
  } else if (allowedCaseTypes.includes('VisaRiskManager')) {
    return ALERT_TABLE_TILE_TABS.VISA_RISK_MANAGER
  }

  return undefined
}

export const getCaseTypesForAlertFilter = (
  alertFilterType: AlertTableTileTab | undefined,
): CaseType[] => {
  if (alertFilterType === ALERT_TABLE_TILE_TABS.MISDIRECTED_DEPOSIT) {
    return ['MisdirectedDeposit']
  } else if (alertFilterType === ALERT_TABLE_TILE_TABS.FIN_CRIMES_TRIAGE) {
    return ['FinCrimes']
  } else if (alertFilterType === ALERT_TABLE_TILE_TABS.PROOF_OF_AUTH) {
    return ['ProofOfAuth']
  } else if (alertFilterType === ALERT_TABLE_TILE_TABS.VISA_RISK_MANAGER) {
    return ['VisaRiskManager']
  }
  return []
}

export const getDefaultStatusesForAlertFilter = (
  alertFilterType?: AlertTableTileTab,
): CaseStepsFilterOptionValue[] => {
  const caseTypes = getCaseTypesForAlertFilter(alertFilterType)
  return getDefaultCaseStepFilterValues(caseTypes)
}
